import {switchMap} from 'rxjs/operators';
import {Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, ParamMap, Router} from '@angular/router';
import {of} from 'rxjs';
import {Location} from '@angular/common';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {CrisisAlertService} from '../../services/crisis-alert.service';
import {Case} from '../../../_models/case.model';
import {Title} from '@angular/platform-browser';
import {Review} from '../../../_models/review.model';
import {ContentTrackingModalComponent} from '../content-tracking-modal/content-tracking-modal.component';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {AssignmentExpirationModalComponent} from '../assignment-expiration-modal/assignment-expiration-modal.component';
import {User} from '../../../_models/user.model';
import {AuthenticationService} from '../../../core/services/authentication.service';
import {DownloadMiniDialogComponent} from '../download-mini-dialog/download-mini-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {ReviewTracking} from '../../../_models/review.tracking.model';
import {Constants} from '../../constants';
import {Attribute} from '../../../_models/attribute.model';

interface Alert {
  type: string;
  message: string;
}

export interface DownloadDialogData {
  target: string;
  url: string;
}

@Component({
  selector: 'app-case-detail',
  templateUrl: './case-detail.component.html',
  styleUrls: ['./case-detail.component.scss']
})
export class CaseDetailComponent implements OnInit, OnDestroy {
  case: Case;
  referenceData: any;
  today: number = Date.now();
  // flags
  loading = true;
  formReadOnly = true;
  private isAlertReleasedThroughProperChannels = false;
  isSpecialityHidden = true;
  specialities: Attribute[];

  private user: User;
  caseId: string;
  private clickSet = new Set();
  form: FormGroup;
  private timeDiff: number;
  nextCaseId: string;
  private MODALS = {tracking: ContentTrackingModalComponent, expiration: AssignmentExpirationModalComponent};

  private expirationTimer: any = null;
  private caseLoadFailTimer: any = null;
  private expiry = 60;
  @ViewChild('formSubmitStatusModal', {static: false}) private formSubmitStatusModal: ElementRef;
  @ViewChild('exitWarningModal', {static: false}) private exitWarningModal: ElementRef;
  @ViewChild('dataFailureModal', {static: false}) private dataFailureModal: ElementRef;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private crisisAlertService: CrisisAlertService,
    private location: Location,
    private title: Title,
    private modalService: NgbModal,
    private authService: AuthenticationService,
    public dialog: MatDialog
  ) {
  }

  ngOnInit() {
    this.loading = true;
    this.crisisAlertService.ensureRefData();
    this.user = JSON.parse(sessionStorage.getItem(Constants.AUTH_TOKEN));
    const userId = this.user.userId;
    this.route.paramMap.pipe(
      switchMap((params: ParamMap) =>
        of(params.get('id'))
      )
    ).subscribe((id) => {
      this.caseId = id;
      this.resetForm(id);
      this.title.setTitle('Case ID - ' + id);
      this.crisisAlertService.getCaseDB(id, userId).subscribe(_case => {
        this.loading = false;
        this.case = _case;
        const part0 = _case.parts[0];
        if (!(this.isAudio(part0.mimeCode) || 'KLTR' === part0.uriSrcType)) {
          this.clickSet.add(1);
        }
        if (_case && _case.assignment && _case.assignment.assignmentType) {
          if ('REVW' === _case.assignment.assignmentType) {
            this.formReadOnly = false;
            this.specialities = this.crisisAlertService.refDataStore.specialities.filter(x => x.name !== _case.specialityName);
            this.expiry = _case.assignment.expiryMinutes;
            this.setAssignmentExpirationTimer();
            try {
              this.form.enable();
            } catch (e) {
              alert('Something crashed - please refresh the page.');
              console.error(e);
            }
          } else {
            this.formReadOnly = true;
          }
          if ('Completed' === this.case.caseStatus) {
            this.form.get('decision').setValue('NCRIS');
          } else if ('Extract Initiated' === this.case.caseStatus
            || 'Extract Failed' === this.case.caseStatus
            || 'Extracted' === this.case.caseStatus
            || 'Ready For Extract' === this.case.caseStatus
            || 'Extract Complete' === this.case.caseStatus
          ) {
            this.form.get('decision').setValue('CRIS');
          }
          console.log('AssignmentID=' + _case.assignment.assignmentId);
          console.log('Assignment Type=' + _case.assignment.assignmentType);
        } else {
          // no assignment means fail case but ensure fail msg is there
          // Also will have to wait 2000 for page to render other wise popup view child will not work.
          const self = this;
          this.caseLoadFailTimer = setTimeout(function () {
            if (self.case && 'data-fail' === self.case.errorMsg) {
              self.modalService.open(self.dataFailureModal,
                {size: 'sm', centered: true, ariaLabelledBy: 'modal-data-failure', backdrop: 'static'});
            }
          }, 2000);
        }
      });
    });
    this.referenceData = this.crisisAlertService.refDataStore;
    this.crisisAlertService.serverTimeObs.subscribe(serverTime => {
      this.today = serverTime;
      this.timeDiff = Date.now() - serverTime;
    });
    setInterval(() => {
      this.today = Date.now() - this.timeDiff;
    }, 15000);
  }

  private isAudio(mimeCode: string): boolean {
    if (!mimeCode) {
      return false;
    }
    const code = mimeCode.toUpperCase();
    return 'MP3' === mimeCode
      || 'OGG' === mimeCode
      || 'WAV' === mimeCode
      || 'M4A' === mimeCode;
  }

  private extendTime() {
    this.crisisAlertService.extendAssignmentForUser(this.case.assignment);
    this.clearAllTimers();
    this.setAssignmentExpirationTimer();
  }

  private setAssignmentExpirationTimer() {
    console.log('Assignment Expiry Minutes = ' + this.expiry);
    const timeout = 1000 * 60 * this.expiry;
    this.expirationTimer = setTimeout(() => this.extendTime(), timeout);
    console.log('Timer started - Assignment Expiry');
  }

  private resetForm(caseId: string): void {
    // all this because angular is not capable of handling SAME component as no ngOnIt().
    this.form = new FormGroup({
      decision: new FormControl({value: '', disabled: true}, Validators.required),
      speciality: new FormControl({value: 'GNRL', disabled: true}),
    });
    this.clickSet = new Set();
    this.isSpecialityHidden = true;
  }

  dashboard() {
    this.isAlertReleasedThroughProperChannels = true;
    if (this.case.assignment) {
      if (this.case.assignment.assignmentType === 'REVW') {
        this.modalService.open(this.exitWarningModal,
          {size: 'sm', centered: true, ariaLabelledBy: 'modal-title-review', backdrop: 'static'});
      } else {
        this.crisisAlertService.releaseAssignmentForUser(this.case.assignment);
        this.goBackToList();
      }
    } else {
      this.goBackToList();
    }
  }

  goBack(): void {
    this.isAlertReleasedThroughProperChannels = true;
    if (this.case.assignment && this.case.assignment.assignmentType) {
      this.crisisAlertService.releaseAssignmentForUser(this.case.assignment);
    }
    this.goBackToList();
  }

  hideSpeciality() {
    this.isSpecialityHidden = true;
  }

  showSpeciality() {
    this.isSpecialityHidden = false;
  }

  onSubmit() {
    this.isAlertReleasedThroughProperChannels = true;
    const currentCase = this.case;
    if (this.checkAllPartsClicked(currentCase.parts.length)) {
      const review = this.createReview(currentCase);
      this.crisisAlertService.saveReview(review).subscribe(x => {
        this.clearAllTimers();
        if (x && x.reviewId) {
          console.log('Released case CaseID=' + this.caseId);
          console.log('API Resp - ReviewID=' + x.reviewId + ' NextCaseId=' + x.nextCaseId);
        } else {
          console.log('API Resp - Review ID  failed !!');
        }
        this.nextCaseId = x.nextCaseId;
        this.clearAllTimers(); // unload - all work done on this case
        this.crisisAlertService.releaseAssignmentForUser(this.case.assignment); // unload
        this.modalService.open(this.formSubmitStatusModal,
          {size: 'sm', centered: true, ariaLabelledBy: 'modal-title-review', backdrop: 'static'});
        return null;
      });
    } else {
      this.modalService.open(this.MODALS['tracking'], {size: 'sm', centered: true, ariaLabelledBy: 'modal-tracking-alert'});
    }
  }

  private createReview(currentCase: Case): Review {
    const review = new Review();
    review.userId = '' + this.user.userId;
    review.caseId = this.caseId;
    review.assignmentId = this.case.assignment.assignmentId;
    review.assignmentType = this.case.assignment.assignmentType;
    review.specialityCode = this.form.value.speciality.toString();
    review.reviewStatusCode = this.form.value.decision.toString();
    review.nextSpecialityCode = this.crisisAlertService.getSpecialityCode(currentCase.specialityName);
    review.commentText = ('DEFR' === review.reviewStatusCode) ? 'defer to ' + review.specialityCode : '';
    return review;
  }

  // pop-up controls only Reviewer can  see
  goBackToList() {
    this.router.navigate(['/cases']); // go back
  }

  // pop-up controls only Reviewer can  see
  goToNextCase() {
    this.router.navigate(['/case', this.nextCaseId]);
  }

  private checkAllPartsClicked(n: number): boolean {
    if ('NCRIS' !== this.form.value.decision.toString()) {
      return true;
    }
    if (this.clickSet.size === 0) {
      return false;
    }
    return ((n * (n + 1)) / 2) === Array.from(this.clickSet).reduce((t: number, x: number) => t + x);
  }

  registerTab(index: number): void {
    if ('FULLBOOK' === this.case.parts[index].partType) {
      this.register(index, 'FULBK');
    } else {
      this.register(index, 'TBCLK');
    }
  }

  register(index: number, action: string) {
    const oldSize = this.clickSet.size;
    this.clickSet.add((index + 1));
    const newSize = this.clickSet.size;
    if (oldSize !== newSize) {
      const tracking = new ReviewTracking();
      tracking.assignmentId = this.case.assignment.assignmentId;
      tracking.userId = this.case.assignment.userId;
      tracking.caseId = this.case.assignment.caseId;
      tracking.contentTypeCode = action;
      tracking.partId = this.case.parts[index].partId;
      this.crisisAlertService.reviewContentTrackingOfUser(tracking);
    }
  }

  @HostListener('window:beforeunload')
  ngOnDestroy(): void {
    this.crisisAlertService.updateCaseStatus('' + this.user.userId, this.caseId, 'NR').subscribe(x => {
      console.log('Released CaseId=' + this.caseId);
    }, error => console.log('Could not Release CaseId=' + this.caseId + ' ' + error));
    this.clearAllTimers();
    if (!this.isAlertReleasedThroughProperChannels) {
      if (this.case.assignment && this.case.assignment.assignmentType) {
        this.crisisAlertService.releaseAssignmentForUser(this.case.assignment);
      }
    }
  }

  @HostListener('contextmenu', ['$event'])
  onRightClick(event) {
    event.preventDefault();
  }

  private clearAllTimers() {
    if (this.expirationTimer) {
      clearTimeout(this.expirationTimer);
      this.expirationTimer = null;
      console.log('Cleared - Assignment Expiry');
    }
    if (this.caseLoadFailTimer) {
      clearTimeout(this.caseLoadFailTimer);
      this.caseLoadFailTimer = null;
      console.log('Cleared - Check Data Fail Timer');
    }
  }

  openDialog(programName: string): void {
    if (programName) {
      const url = this.crisisAlertService.refDataStore.programs.find(item => item.name === programName).guidelinesUrl;
      this.crisisAlertService.getFileFromS3(url).subscribe(signedUrl => {
        const dialogRef = this.dialog.open(DownloadMiniDialogComponent, {
          width: '10px',
          data: {target: '_blank', url: signedUrl}
        });
        dialogRef.afterClosed().subscribe(result => {
          console.log('mini dialog closed');
        });
      });
    }
  }

  moveCaseGoBack() {
    // technical error case.
    this.crisisAlertService.updateCaseStatus('' + this.user.userId, this.caseId, 'TD').subscribe(x => {
      console.log('Released CaseId=' + this.caseId);
    }, error => console.log('Could not Release CaseId=' + this.caseId + ' ' + error));
    // not sure if assignment data could even reach UI if failed at server.
    if (this.case.assignment && this.case.assignment.assignmentType) {
      this.crisisAlertService.releaseAssignmentForUser(this.case.assignment);
    }
    this.goBackToList();
  }

  registerAudio($event: boolean, index: number) {
    if ($event) {
      this.register(index, 'PLAY');
    }
  }

  registerKaltura($event: boolean, index: number) {
    if ($event) {
      this.register(index, 'PLAY');
    }
  }
}
